import React from 'react';
import styled, { css } from 'styled-components';
import { Colors } from '@prezi/dragon';

import Tick from '../Icons/Tick';
import ChevronRight from '../Icons/ChevronRight';
import Dropdown from '../Dropdown/Dropdown';
import DropdownList from '../Dropdown/DropdownList';
import DropdownItem from '../Dropdown/DropdownItem';
import { measureElement } from '../../utilities';

const disabledCss = css`
    color: ${Colors.neutral200};
    cursor: not-allowed;
`;

export type TriggerProps = {
    disabled: boolean;
};

export type OptionProps<T> = {
    value: T;
    label: string;
    disabled?: boolean;
    isSelected: boolean;
    onClick: () => void;
    dataLookup?: string;
};

export type SelectOptionType<T> = {
    label: string;
    value: T;
    disabled?: boolean;
    Component?: React.FC<OptionProps<T>>;
};

type Props<T> = {
    value: T;
    options: SelectOptionType<T>[];
    portalRoot?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    onChange?: (value: T) => void;
    TriggerComponent?: React.FC<TriggerProps>;
    OptionComponent?: React.FC<OptionProps<T>>;
    placeholder?: string;
    dataLookup?: string;
};

function valueEquals<T>(value: T) {
    return (option: SelectOptionType<T>) => option.value === value;
}

const Wrapper = styled.div`
    position: relative;
`;

const OptionLabel = styled.div<{ disabled: boolean }>`
    ${p => p.disabled && disabledCss}
`;

function Option<T>({ label, disabled = false, isSelected, onClick, dataLookup }: OptionProps<T>) {
    return (
        <DropdownItem
            iconLeft={isSelected ? <Tick width={16} height={16} /> : <SelectIconPlaceholder />}
            onClick={() => !disabled && onClick()}
            dataLookup={dataLookup}
        >
            <OptionLabel disabled={disabled}>{label}</OptionLabel>
        </DropdownItem>
    );
}

const Trigger = styled.div<TriggerProps>`
    display: flex;
    align-items: center;
    padding: 8px;
    cursor: pointer;
    ${p => p.disabled && disabledCss}
`;

const TriggerLabel = styled.span`
    margin-right: 6px;
    color: inherit;
    line-height: 19px;
`;

export const SelectIconPlaceholder = styled.div`
    width: 16px;
    height: 16px;
`;

export const ArrowContainer = styled.div<{ isOpen: boolean }>`
    display: inline-flex;
    width: 16px;
    height: 16px;
    cursor: pointer;
    transform: ${({ isOpen }) => (isOpen ? `rotate(-90deg)` : `rotate(90deg)`)};
`;

const DropdownWrap = styled.div<{ fullWidth: boolean }>`
    position: relative;

    & > div {
        ${p => p.fullWidth && 'width: 100%;'}
    }
`;

function Select<T>({
    value,
    options,
    placeholder,
    TriggerComponent,
    OptionComponent,
    disabled = false,
    fullWidth = false,
    portalRoot,
    onChange = () => {},
    dataLookup,
}: Props<T>) {
    const mountedRef = React.useRef(true);
    const [itemHeight, setItemHeight] = React.useState(0);
    const [isOpen, setOpen] = React.useState<boolean>(false);
    const triggerRef = React.useRef(null);

    const SelectTrigger = TriggerComponent || Trigger;
    const SelectOption = OptionComponent || Option;

    React.useEffect(() => {
        // to be able to use a virtualised dropdown scroll container, need to measure a dummy item's height
        //  to calculate sizes inside the dropdown list
        if (options.length) {
            const optionElement = <SelectOption {...options[0]} isSelected={false} onClick={() => {}} />;
            measureElement(optionElement, ({ height }) => {
                if (mountedRef.current) {
                    setItemHeight(height);
                }
            });
        }

        return () => {
            mountedRef.current = false;
        };
    }, [options.length]);

    return (
        <Wrapper>
            <SelectTrigger
                ref={triggerRef}
                disabled={disabled}
                onClick={() => !disabled && setOpen(!isOpen)}
                data-lookup={dataLookup}
            >
                <TriggerLabel>{options.find(valueEquals(value))?.label || placeholder}</TriggerLabel>
                <ArrowContainer isOpen={isOpen}>
                    <ChevronRight fill={disabled ? Colors.neutral200 : Colors.neutral800} />
                </ArrowContainer>
            </SelectTrigger>
            <DropdownWrap fullWidth={fullWidth}>
                <Dropdown
                    isOpen={isOpen}
                    triggerRef={triggerRef}
                    portalRoot={portalRoot}
                    fullWidth={fullWidth}
                    onClose={() => setOpen(false)}
                >
                    <DropdownList itemHeight={itemHeight} height={Math.min(6, options.length) * itemHeight}>
                        {options.map(({ value: optionValue, label, disabled, Component }, i) => {
                            const SelectOptionComponent = Component || SelectOption;
                            return (
                                <SelectOptionComponent
                                    key={i}
                                    value={optionValue}
                                    label={label}
                                    disabled={disabled}
                                    isSelected={optionValue === value}
                                    onClick={() => {
                                        onChange(optionValue);
                                        setOpen(false);
                                    }}
                                    dataLookup={dataLookup ? dataLookup + '-option-' + i : undefined}
                                />
                            );
                        })}
                    </DropdownList>
                </Dropdown>
            </DropdownWrap>
        </Wrapper>
    );
}

export default Select;
